import React, { useEffect, useState, useRef } from "react";
import { Modal, Tab, Col, Nav, Button } from "react-bootstrap";
import {
  Account, Attachment, MessageHandlerDisplayType, prettyBreadcrumbs, TreeItem, useAppDispatch, MailErrorMessages, Message, MessageAnchors, TreeItemType,
  UploadRequest, useFetchLink
} from "src/common";
import { Icon } from "src/components";
import { DriveTabContent, StarredTabContent } from "./";
import { driveActions, uiActions } from "src/store";

type AttachmentModalTabType = "my_preveil" | "starred";
const AttachmentModalTabs: { [key in AttachmentModalTabType]: AttachmentModalTabType } = {
    my_preveil: "my_preveil",
    starred: "starred",
};

type AllProps = {
    current_account: Account;
    current_attachments?: Attachment[];
    handleHideModal: () => void;
}

function AttachmentModalComponent(props: AllProps) {
    const { current_account, current_attachments, handleHideModal } = props;
    const dispatch = useAppDispatch();
    const has_attachments = !!current_attachments && current_attachments.length > 0;
    const [show, setShow] = useState<boolean>(has_attachments);
    const [selected_tab, setSelectedTab] = useState<AttachmentModalTabType>(AttachmentModalTabs.my_preveil);
    const [destination, setDestination] = useState<TreeItem>();
    const link_upload_ref = useRef<UploadRequest | undefined>();
    const { link_entity, error: link_error, fetchLink, resetLink } = useFetchLink(current_account);

    useEffect(() => {
        dispatch(uiActions.setUploadProgressHidden(false));
        return () => {
            dispatch(uiActions.setUploadProgressHidden(true));
        };
    }, []);

    // Description: Get attachment details from current_thread messages then open modal else throw error
    useEffect(() => {
        const next_show = !!current_attachments && current_attachments.length > 0;
        setShow(next_show);
        if (next_show) {
            setDestination(undefined);
            setSelectedTab(AttachmentModalTabs.my_preveil);
        }
    }, [current_attachments]);

    // Description: Complete round trip for links from Starred tab (these are missing the linked_collection_id)
    useEffect(() => {
        if (!!link_entity && !!link_upload_ref.current) {
            const upload = Object.assign(link_upload_ref.current, { linked_collection_id: link_entity.getLinkedCollectionId_asB64() });
            handleDownloadComplete(upload);
        }

        !!link_error && handleDownloadErrors();
    }, [link_entity, link_error]);

    // Description: Handle Download to destination
    function handleInitDownload() {
        const collection_id = destination?.collection_id;
        const id = destination?.id;
        if (!!collection_id && !!id && !!current_attachments && current_attachments.length > 0) {
            const files: File[] = current_attachments
                .filter(attachment => !!attachment.blob)
                .map(attachment => new File([attachment.blob as Blob], attachment.filename, {
                    type: (attachment.blob as Blob).type,
                    lastModified: new Date().getTime() // Optional: Set last modified date
                }));
            if (files.length === 0) {
                handleDownloadErrors();
                return;
            }
            const upload = {
                collection_id,
                node_id: id,
                linked_collection_id: destination.linked_collection_id,
                files,
                destination: { id },
                is_download: true // Use this flag to set the title on the Upload Progress to downloaded instead of uploaded
            };

            // NOTE: If we have a link node as destination but it is missing the linked_collection_id, we need to FetchLink info
            if (destination.type === TreeItemType.link && !destination.linked_collection_id) {
                link_upload_ref.current = upload;
                fetchLink(collection_id, id);
                return;
            }
            handleDownloadComplete(upload);
        } else {
            handleDownloadErrors();
        }
    }

    // Description: handle Download when all data is collected
    function handleDownloadComplete(upload: UploadRequest) {
        dispatch(driveActions.setFileUploadRequest(upload));
        resetModal();
    }

    // Description: handle Download when all data is collected
    function handleDownloadErrors(_message?: string) {
        const message = _message ?? MailErrorMessages.error_downloading_attachment.replace(
            MessageAnchors.file_name,
            ((!!current_attachments && current_attachments[0]?.filename) || "the selected attachments"),
        );
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr), { current_attachments, destination }));
        resetModal();
    }

    // Description: Reset current state to start fresh on open
    function resetModal() {
        setDestination(undefined);
        setSelectedTab(AttachmentModalTabs.my_preveil);
        handleHideModal();
        resetLink();
    }

    return <Modal
        size="lg"
        dialogClassName="move-modal attachment-download"
        show={show}
        onHide={resetModal}
        aria-label="Download Attachment"
        >
        <Modal.Header className="align-items-start" closeButton>
            <div>
                <p>
                    Download {((current_attachments?.length || 0) > 1) ? "Files" : "File"}: <b>{
                        (current_attachments?.length || 0) > 1 ?
                            `${current_attachments?.length || 0} attachments` :
                            current_attachments?.[0]?.filename
                    }</b>&nbsp;
                    {
                        !!destination?.name &&
                        <>
                            to:&nbsp;
                            <span
                                className="text-link"
                                dangerouslySetInnerHTML={{ __html: prettyBreadcrumbs(destination.path) }}
                            />
                        </>
                    }
                </p>
                <p className="text-secondary">
                    Select a folder in Drive <b>{selected_tab === AttachmentModalTabs.starred ? "Starred" : "My Preveil"}</b> to download your file into.
                </p>
            </div>
        </Modal.Header>
        <Modal.Body className="g-0 row">
            <Tab.Container defaultActiveKey={AttachmentModalTabs.my_preveil}>
                <Col sm={4} className="left-navigation bg-light">
                    <Nav variant="pills" className="flex-column">
                        <Nav.Item>
                            <Nav.Link eventKey={AttachmentModalTabs.my_preveil}  >
                                <Icon className="ficon-folder" />
                                <span>My Preveil</span>
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey={AttachmentModalTabs.starred} >
                                <Icon className="ficon-star" />
                                <span>Starred</span>
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </Col>
                <Col sm={8} className="right-col">
                    <Tab.Content>
                        <Tab.Pane eventKey={AttachmentModalTabs.my_preveil}>
                            <DriveTabContent {...props} setDestination={setDestination} />
                        </Tab.Pane>
                        <Tab.Pane eventKey={AttachmentModalTabs.starred}>
                            <StarredTabContent {...props} setDestination={setDestination} />
                        </Tab.Pane>
                    </Tab.Content>
                </Col>
            </Tab.Container>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
            <Button onClick={handleInitDownload} disabled={!destination} >
                Download
            </Button>
            <Button variant="no-outline-primary" onClick={resetModal}>
                Cancel
            </Button>
        </Modal.Footer>
    </Modal>;
}

export default React.memo(AttachmentModalComponent);
